import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { CreditCard, CheckCircle, AlertCircle, Save, Info } from 'lucide-react';

const BankingInfoSection = ({ profData, onUpdate }) => {
  const [bankData, setBankData] = useState({
    iban: '',
    bank_name: '',
    account_holder: '',
    country: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    if (profData) {
      setBankData({
        iban: profData.iban_maroc || profData.iban || '',
        bank_name: profData.bank_name || '',
        account_holder: profData.account_holder || `${profData.prenom || ''} ${profData.nom || ''}`.trim(),
        country: profData.country || ''
      });
    }
  }, [profData]);

  const validateIBAN = (iban) => {
    const clean = iban.replace(/\s/g, '').toUpperCase();
    if (!clean) return 'L\'IBAN est requis';
    if (clean.length < 15 || clean.length > 34) return 'IBAN invalide (15 à 34 caractères)';
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(clean)) return 'L\'IBAN doit commencer par 2 lettres de pays + 2 chiffres';
    // Contrôle de la clé mod-97 (détecte ~99% des fautes de frappe)
    const rearranged = clean.slice(4) + clean.slice(0, 4);
    const expanded = rearranged.replace(/[A-Z]/g, ch => (ch.charCodeAt(0) - 55).toString());
    let remainder = 0;
    for (let i = 0; i < expanded.length; i++) {
      remainder = (remainder * 10 + (expanded.charCodeAt(i) - 48)) % 97;
    }
    if (remainder !== 1) return 'IBAN invalide (clé de contrôle erronée — vérifiez la saisie)';
    return null;
  };

  const formatIBAN = (value) => {
    const clean = value.replace(/\s/g, '').toUpperCase();
    const chunks = clean.match(/.{1,4}/g) || [];
    return chunks.join(' ').substring(0, 42);
  };

  const detectCountryFromIBAN = (iban) => {
    const clean = iban.replace(/\s/g, '').toUpperCase();
    const countryCodes = {
      'MA': 'Maroc', 'DZ': 'Algérie', 'TN': 'Tunisie', 'LY': 'Libye',
      'EG': 'Égypte', 'SD': 'Soudan', 'MR': 'Mauritanie',
      'SA': 'Arabie Saoudite', 'AE': 'Émirats Arabes Unis', 'QA': 'Qatar',
      'KW': 'Koweït', 'BH': 'Bahreïn', 'OM': 'Oman', 'JO': 'Jordanie',
      'LB': 'Liban', 'IQ': 'Irak', 'PS': 'Palestine', 'YE': 'Yémen',
      'TR': 'Turquie',
      'FR': 'France', 'DE': 'Allemagne', 'BE': 'Belgique', 'NL': 'Pays-Bas',
      'ES': 'Espagne', 'IT': 'Italie', 'PT': 'Portugal', 'GB': 'Royaume-Uni',
      'CH': 'Suisse', 'AT': 'Autriche', 'LU': 'Luxembourg', 'IE': 'Irlande',
      'MC': 'Monaco', 'LI': 'Liechtenstein', 'MT': 'Malte', 'CY': 'Chypre',
      'GR': 'Grèce', 'FI': 'Finlande', 'SE': 'Suède', 'NO': 'Norvège',
      'DK': 'Danemark', 'IS': 'Islande',
      'PL': 'Pologne', 'CZ': 'République Tchèque', 'SK': 'Slovaquie',
      'HU': 'Hongrie', 'RO': 'Roumanie', 'BG': 'Bulgarie', 'HR': 'Croatie',
      'SI': 'Slovénie', 'RS': 'Serbie', 'BA': 'Bosnie', 'ME': 'Monténégro',
      'MK': 'Macédoine', 'AL': 'Albanie', 'XK': 'Kosovo',
      'EE': 'Estonie', 'LV': 'Lettonie', 'LT': 'Lituanie',
      'UA': 'Ukraine', 'BY': 'Biélorussie', 'MD': 'Moldavie',
      'GE': 'Géorgie', 'AM': 'Arménie', 'AZ': 'Azerbaïdjan',
      'SN': 'Sénégal', 'CI': "Côte d\'Ivoire", 'CM': 'Cameroun',
      'GH': 'Ghana', 'NG': 'Nigeria', 'KE': 'Kenya', 'ET': 'Éthiopie',
      'TZ': 'Tanzanie', 'UG': 'Ouganda', 'RW': 'Rwanda', 'MZ': 'Mozambique',
      'ZA': 'Afrique du Sud', 'MG': 'Madagascar', 'MU': 'Maurice',
      'IN': 'Inde', 'PK': 'Pakistan', 'BD': 'Bangladesh', 'LK': 'Sri Lanka',
      'PH': 'Philippines', 'ID': 'Indonésie', 'MY': 'Malaisie',
      'SG': 'Singapour', 'HK': 'Hong Kong',
      'BR': 'Brésil', 'MX': 'Mexique', 'AR': 'Argentine', 'CO': 'Colombie',
      'CL': 'Chili', 'PE': 'Pérou', 'VE': 'Venezuela',
      'US': 'États-Unis', 'CA': 'Canada'
    };
    const code = clean.substring(0, 2);
    return countryCodes[code] || '';
  };

  const handleIBANChange = (e) => {
    const formatted = formatIBAN(e.target.value);
    const country = detectCountryFromIBAN(formatted);
    setBankData(prev => ({ ...prev, iban: formatted, country }));
    const error = validateIBAN(formatted);
    setErrors(prev => ({ ...prev, iban: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ibanError = validateIBAN(bankData.iban);
    if (ibanError) { setErrors({ iban: ibanError }); return; }
    if (!bankData.account_holder.trim()) { setErrors({ account_holder: 'Le nom du titulaire est requis' }); return; }

    setLoading(true);
    setErrors({});

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Token manquant');

      const response = await fetch(`${API_BASE_URL}/profs/update-banking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          iban: bankData.iban.replace(/\s/g, ''),
          bank_name: bankData.bank_name.trim() || null,
          account_holder: bankData.account_holder.trim(),
          country: bankData.country || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la sauvegarde');
      }

      const result = await response.json();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      if (onUpdate) onUpdate(result.data);

    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  const hasIBAN = bankData.iban && !errors.iban;

  return (
    <div className="banking-info-section">
      <div className="banking-header">
        <div className="banking-icon"><CreditCard size={24} /></div>
        <h2>Informations Bancaires</h2>
        {hasIBAN && (
          <span className="rib-status-badge configured">
            <CheckCircle size={12} />
            IBAN configuré {bankData.country ? `— ${bankData.country}` : ''}
          </span>
        )}
      </div>

      <div className="banking-info-box">
        <div className="info-icon"><Info size={16} /></div>
        <div className="info-content">
          <p className="info-title">Informations importantes :</p>
          <ul>
            <li>Votre IBAN est nécessaire pour recevoir vos paiements mensuels</li>
            <li>Tous les pays sont acceptés — Maroc, France, Égypte, Europe et plus</li>
            <li>Ces informations sont sécurisées et utilisées uniquement pour les paiements</li>
          </ul>
        </div>
      </div>

      <div className="banking-form">
        <div className="form-field">
          <label className="form-label">IBAN <span className="required">*</span></label>
          <input
            type="text"
            value={bankData.iban}
            onChange={handleIBANChange}
            placeholder="FR76 3000 6000 0112 3456 7890 189"
            className={`form-input iban-input ${errors.iban ? 'error' : ''}`}
            maxLength={42}
          />
          {errors.iban && <p className="error-message"><AlertCircle size={14} />{errors.iban}</p>}
          {bankData.country && <p className="field-help" style={{color: '#22c55e'}}>✓ Pays détecté : {bankData.country}</p>}
          <p className="field-help">Format : 2 lettres pays + 2 chiffres + numéro de compte</p>
        </div>

        <div className="form-field">
          <label className="form-label">Nom de la banque</label>
          <input
            type="text"
            value={bankData.bank_name}
            onChange={(e) => setBankData(prev => ({ ...prev, bank_name: e.target.value }))}
            placeholder="Ex: CIH Bank, BNP Paribas, Banque Misr..."
            className="form-input"
          />
          <p className="field-help">Optionnel</p>
        </div>

        <div className="form-field">
          <label className="form-label">Nom du titulaire <span className="required">*</span></label>
          <input
            type="text"
            value={bankData.account_holder}
            onChange={(e) => setBankData(prev => ({ ...prev, account_holder: e.target.value }))}
            placeholder="Nom complet tel qu'il apparaît sur le compte"
            className={`form-input ${errors.account_holder ? 'error' : ''}`}
          />
          {errors.account_holder && <p className="error-message"><AlertCircle size={14} />{errors.account_holder}</p>}
        </div>

        {errors.submit && <div className="error-box"><AlertCircle size={16} /><p>{errors.submit}</p></div>}
        {showSuccess && <div className="success-box"><CheckCircle size={16} /><p>Informations bancaires mises à jour avec succès !</p></div>}

        <div className="form-submit">
          <button
            onClick={handleSubmit}
            disabled={loading || !bankData.iban || !bankData.account_holder || !!errors.iban}
            className="submit-button"
          >
            <Save size={16} />
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BankingInfoSection;
